import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { papeisApi, type SalvarPapelRequest } from '../api/papeis';
import { useIsAuthenticated } from './useAuth';
import { usuariosKeys } from './useUsuarios';

export const papeisKeys = {
  all: ['papeis'] as const,
  lists: () => [...papeisKeys.all, 'list'] as const,
};

export function usePapeis() {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuery({
    queryKey: papeisKeys.lists(),
    queryFn: () => papeisApi.listar(),
    select: (response) => response.data.data,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Invalida papéis E usuários: alterar um papel muda as permissões efetivas de
 * quem está vinculado, então a lista de usuários (que mostra papel e o badge
 * "Sem acesso") precisa recarregar junto.
 */
function useInvalidarPapeis() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: papeisKeys.all });
    qc.invalidateQueries({ queryKey: usuariosKeys.all });
  };
}

export function useCriarPapel() {
  const invalidar = useInvalidarPapeis();
  return useMutation({
    mutationFn: (data: SalvarPapelRequest) => papeisApi.criar(data),
    onSuccess: invalidar,
  });
}

export function useAtualizarPapel() {
  const invalidar = useInvalidarPapeis();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SalvarPapelRequest }) => papeisApi.atualizar(id, data),
    onSuccess: invalidar,
  });
}

export function useExcluirPapel() {
  const invalidar = useInvalidarPapeis();
  return useMutation({
    mutationFn: (id: number) => papeisApi.excluir(id),
    onSuccess: invalidar,
  });
}

export function useAtribuirPapel() {
  const invalidar = useInvalidarPapeis();
  return useMutation({
    mutationFn: ({ usuarioId, papelId }: { usuarioId: number; papelId: number | null }) =>
      papeisApi.atribuir(usuarioId, papelId),
    onSuccess: invalidar,
  });
}
